import { useRef, useState } from "react";
import { listProfileOverrides } from "../../db";
import { downloadBlob } from "../../engine/export";
import { buildZipArchive } from "../../engine/bundle-zip";
import { PROFILES } from "../../profiles";
import { resolveEffectiveProfile, type ResolvedSourceProfile } from "../../profiles/resolve";
import { formatBytes } from "../upload/pair-size";
import { batchZipFileName, buildBatchArchiveEntries } from "./batch-bundle";
import { runBatch, type BatchProgress, type BatchResult } from "./run-batch";

/**
 * Analyse a folder of runs without opening each pair by hand.
 *
 * This is `npm run analyze` for someone with no terminal: the same pipeline,
 * the same artifacts, one zip. It records no decisions and opens no results —
 * a batch answers "which of these runs needs a person", and the answer sends
 * you back to the upload screen for the one that does.
 *
 * The work happens in its own worker, one pair at a time, and the worker is
 * created when a batch starts and terminated when it ends: a folder of runs is
 * hundreds of megabytes, and nothing about it should outlive the run.
 */
export function BatchPanel() {
  const folderInput = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<BatchProgress | null>(null);
  const [result, setResult] = useState<BatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const start = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    setDownloaded(false);
    // Resolution stays on the main thread: overrides live in IndexedDB, and a
    // worker-side lookup could race an override edit mid-batch.
    const overrides = await listProfileOverrides().catch(() => []);
    const overridesById = new Map(overrides.map((override) => [override.profileId, override]));
    const profiles: ResolvedSourceProfile[] = Object.values(PROFILES).map(
      (profile) => resolveEffectiveProfile(profile, overridesById.get(profile.id) ?? null).profile
    );

    const worker = new Worker(new URL("../../workers/batch.worker.ts", import.meta.url), { type: "module" });
    try {
      const batch = await runBatch({
        worker,
        files: files.map((file) => ({ fileName: file.name, bytes: file.size, text: () => file.text() })),
        profiles,
        generatedAt: new Date().toISOString(),
        onProgress: setProgress
      });
      setResult(batch);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The batch could not be completed.");
    } finally {
      worker.terminate();
      setRunning(false);
    }
  };

  const download = (batch: BatchResult) => {
    const generatedAt = new Date().toISOString();
    const entries = buildBatchArchiveEntries(batch, generatedAt);
    const archive = buildZipArchive(entries);
    setDownloaded(downloadBlob(new Blob([archive as BlobPart], { type: "application/zip" }), batchZipFileName(generatedAt)));
  };

  const analysed = result?.outcomes.filter((outcome) => outcome.status === "analysed") ?? [];

  return (
    <section className="space-y-3 rounded-sm border bg-white p-4" data-testid="batch-panel">
      <div>
        <h3 className="font-medium">Analyse a folder of runs</h3>
        <p className="mt-1 text-sm text-slate-600">
          Every source in the folder, compared run to run, handed back as one zip. Nothing is decided
          and nothing is recorded — this says which runs need a person, and you open those here.
        </p>
      </div>

      <input
        className="hidden"
        data-testid="batch-folder-input"
        multiple
        onChange={(event) => {
          setFiles([...(event.target.files ?? [])].filter((file) => file.name.toLowerCase().endsWith(".json")));
          setResult(null);
          setError(null);
        }}
        ref={(node) => {
          folderInput.current = node;
          // React has no prop for this; the attribute is what makes a folder
          // picker a folder picker.
          if (node) node.setAttribute("webkitdirectory", "");
        }}
        type="file"
      />

      <div className="flex flex-wrap items-center gap-3">
        <button
          className="rounded-sm border border-slate-300 bg-white px-3 py-1 text-sm font-medium hover:bg-slate-50 disabled:opacity-60"
          data-testid="batch-choose-folder"
          disabled={running}
          onClick={() => folderInput.current?.click()}
          type="button"
        >
          Choose a folder…
        </button>
        <button
          className="rounded-sm bg-sky-700 px-3 py-1 text-sm text-white disabled:bg-slate-400"
          data-testid="batch-run"
          disabled={running || files.length < 2}
          onClick={() => void start()}
          type="button"
        >
          {running ? "Analysing…" : "Analyse the folder"}
        </button>
        {files.length > 0 ? (
          <p className="text-sm text-slate-600" data-testid="batch-file-count">
            {files.length} JSON {files.length === 1 ? "file" : "files"} selected (
            {formatBytes(files.reduce((total, file) => total + file.size, 0))})
          </p>
        ) : null}
      </div>

      {running && progress ? (
        <p aria-live="polite" className="text-sm text-slate-700" data-testid="batch-progress">
          {progress.label} — {progress.done} of {progress.total}
        </p>
      ) : null}

      {error ? (
        <p className="rounded-sm border border-red-300 bg-red-50 p-3 text-sm text-red-700" data-testid="batch-error">
          {error}
        </p>
      ) : null}

      {result ? (
        <div className="space-y-3" data-testid="batch-result">
          <p className="text-sm">
            {analysed.length} of {result.plan.pairs.length} planned {result.plan.pairs.length === 1 ? "pair" : "pairs"}{" "}
            analysed.
          </p>

          {result.outcomes.length > 0 ? (
            <table className="w-full text-sm" data-testid="batch-results-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="pb-1 font-medium">Source</th>
                  <th className="pb-1 font-medium">Compared</th>
                  <th className="pb-1 font-medium">Verdict</th>
                  <th className="pb-1 font-medium">Findings</th>
                </tr>
              </thead>
              <tbody>
                {result.outcomes.map((outcome) => (
                  <tr className="border-t align-top" data-testid={`batch-row:${outcome.profileId}`} key={outcome.pairId}>
                    <th className="py-1 pr-3 text-left font-medium" scope="row">
                      {outcome.profileId}
                    </th>
                    <td className="py-1 pr-3 text-xs text-slate-600">
                      {outcome.referenceFileName} → {outcome.candidateFileName}
                      {outcome.runsAvailable > 2 ? (
                        <span className="block">The two newest of {outcome.runsAvailable} runs in the folder.</span>
                      ) : null}
                    </td>
                    <td className="py-1 pr-3">
                      {outcome.status === "analysed" ? (
                        <span className={outcome.verdict === "Quarantined" ? "text-red-700" : "text-slate-700"}>
                          {outcome.verdict}
                        </span>
                      ) : (
                        <span className="text-amber-800">{outcome.status === "refused" ? "Refused" : "Failed"}</span>
                      )}
                    </td>
                    <td className="py-1 text-slate-700">
                      {outcome.status === "analysed" ? outcome.findingCount : outcome.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}

          {result.plan.skipped.length > 0 ? (
            <div className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="batch-skipped">
              <p className="font-medium">Not analysed</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {result.plan.skipped.map((group) => (
                  <li key={`${group.profileId ?? "none"}:${group.fileNames.join(",")}`}>
                    <span className="font-medium">{group.profileId ?? "Unidentified"}</span> ({group.fileNames.join(", ")}) —{" "}
                    {group.reason}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {analysed.length > 0 ? (
            <div className="flex items-center gap-3">
              <button
                className="rounded-sm border border-slate-300 bg-white px-3 py-1 text-sm font-medium hover:bg-slate-50"
                data-testid="batch-download"
                onClick={() => download(result)}
                type="button"
              >
                Download the batch as one zip
              </button>
              {downloaded ? <span className="text-sm text-slate-600">Download started.</span> : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
